import { useCallback, useEffect, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  ExternalLink,
  Loader2,
  Play,
  RefreshCw,
  XCircle,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { cn } from '../../lib/utils';
import {
  ContaAzulStatus,
  fetchContaAzulStatus,
  getOAuthAuthorizeUrl,
  triggerIncrementalSync,
  triggerReconciliationSync,
} from '../../services/conta-azul.service';

export function ContaAzulPage() {
  const [status, setStatus] = useState<ContaAzulStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState<'incremental' | 'reconciliation' | null>(null);

  const loadStatus = useCallback(async () => {
    try {
      setError(null);
      const data = await fetchContaAzulStatus();
      setStatus(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadStatus();
    const interval = setInterval(loadStatus, 30_000);
    return () => clearInterval(interval);
  }, [loadStatus]);

  const handleSync = useCallback(async (type: 'incremental' | 'reconciliation') => {
    setSyncing(type);
    try {
      if (type === 'incremental') {
        await triggerIncrementalSync();
      } else {
        await triggerReconciliationSync();
      }
      await loadStatus();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao sincronizar');
    } finally {
      setSyncing(null);
    }
  }, [loadStatus]);

  const tokenValid = status?.contaAzulAuth?.isValid ?? false;
  const needsReauth = (status?.daysUntilReauth ?? 999) <= 10;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Conta Azul + Trello</h1>
          <p className="text-sm text-muted-foreground">
            Sincronizacao automatica de vendas do Conta Azul para o Trello
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={loadStatus}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Atualizar
        </Button>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
          {error}
        </div>
      )}

      {/* Reauth warning banner */}
      {(!tokenValid || needsReauth) && (
        <div className={cn(
          "rounded-lg border p-4 flex items-start gap-3",
          !tokenValid
            ? "border-destructive/50 bg-destructive/10"
            : "border-yellow-500/50 bg-yellow-500/10"
        )}>
          <AlertTriangle className={cn(
            "h-5 w-5 mt-0.5 shrink-0",
            !tokenValid ? "text-destructive" : "text-yellow-600"
          )} />
          <div className="flex-1">
            <p className="font-medium">
              {!tokenValid
                ? 'Token expirado — sincronizacao parada!'
                : `Re-autorizacao necessaria em ${status?.daysUntilReauth} dias`
              }
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              Clique no botao abaixo para re-autorizar o acesso ao Conta Azul.
              Voce sera redirecionado para fazer login e o token sera renovado automaticamente.
            </p>
          </div>
          <Button
            onClick={() => window.open(getOAuthAuthorizeUrl(), '_blank')}
            variant={!tokenValid ? "destructive" : "outline"}
            size="sm"
          >
            <ExternalLink className="h-4 w-4 mr-2" />
            Re-autorizar
          </Button>
        </div>
      )}

      <div className="grid gap-6 md:grid-cols-2">
        {/* Auth Status Card */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Autenticacao</CardTitle>
            <CardDescription>Status do token OAuth do Conta Azul</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Token</span>
              {tokenValid ? (
                <Badge variant="outline" className="bg-green-500/10 text-green-700 border-green-500/30">
                  <CheckCircle2 className="h-3 w-3 mr-1" />
                  Valido
                </Badge>
              ) : (
                <Badge variant="outline" className="bg-destructive/10 text-destructive border-destructive/30">
                  <XCircle className="h-3 w-3 mr-1" />
                  Expirado
                </Badge>
              )}
            </div>

            {status?.contaAzulAuth?.expiresAt && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Expira em</span>
                <span className="text-sm font-mono">
                  {new Date(status.contaAzulAuth.expiresAt).toLocaleString('pt-BR')}
                </span>
              </div>
            )}

            {status?.daysSinceAuth != null && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Autorizado ha</span>
                <span className="text-sm">
                  {status.daysSinceAuth} dias
                </span>
              </div>
            )}

            {status?.daysUntilReauth != null && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Re-auth em</span>
                <span className={cn("text-sm font-medium", needsReauth && "text-yellow-600")}>
                  ~{status.daysUntilReauth} dias
                </span>
              </div>
            )}

            <Button
              className="w-full mt-2"
              variant={tokenValid ? "outline" : "default"}
              onClick={() => window.open(getOAuthAuthorizeUrl(), '_blank')}
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              {tokenValid ? 'Re-autorizar (preventivo)' : 'Autorizar Conta Azul'}
            </Button>
          </CardContent>
        </Card>

        {/* Integration Status Card */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Integracao</CardTitle>
            <CardDescription>Configuracao e ultimo sync</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Integracao</span>
              <Badge variant="outline" className={status?.enabled
                ? "bg-green-500/10 text-green-700 border-green-500/30"
                : "bg-muted text-muted-foreground"
              }>
                {status?.enabled ? 'Ativa' : 'Desativada'}
              </Badge>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Trello</span>
              <Badge variant="outline" className={status?.trelloConfigured
                ? "bg-green-500/10 text-green-700 border-green-500/30"
                : "bg-destructive/10 text-destructive border-destructive/30"
              }>
                {status?.trelloConfigured ? 'Configurado' : 'Nao configurado'}
              </Badge>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Ultimo sync incremental</span>
              <span className="text-sm font-mono">
                {status?.lastIncrementalSync === 'Never'
                  ? 'Nunca'
                  : new Date(status?.lastIncrementalSync ?? '').toLocaleString('pt-BR')}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Ultimo reconciliacao</span>
              <span className="text-sm font-mono">
                {status?.lastReconciliationSync === 'Never'
                  ? 'Nunca'
                  : new Date(status?.lastReconciliationSync ?? '').toLocaleString('pt-BR')}
              </span>
            </div>

            <div className="flex gap-2 mt-2">
              <Button
                variant="outline"
                size="sm"
                className="flex-1"
                disabled={!tokenValid || syncing !== null}
                onClick={() => handleSync('incremental')}
              >
                {syncing === 'incremental'
                  ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  : <Play className="h-4 w-4 mr-1" />}
                Sync Incremental
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="flex-1"
                disabled={!tokenValid || syncing !== null}
                onClick={() => handleSync('reconciliation')}
              >
                {syncing === 'reconciliation'
                  ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  : <Play className="h-4 w-4 mr-1" />}
                Reconciliacao
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Sync Activity */}
      {status?.recentSyncs && status.recentSyncs.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Atividade Recente</CardTitle>
            <CardDescription>Ultimas sincronizacoes</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {status.recentSyncs.map((sync) => (
                <div
                  key={sync.id}
                  className="flex items-center gap-3 rounded-md border px-3 py-2 text-sm"
                >
                  {sync.status === 'COMPLETED' ? (
                    <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
                  ) : sync.status === 'FAILED' ? (
                    <XCircle className="h-4 w-4 text-destructive shrink-0" />
                  ) : (
                    <Loader2 className="h-4 w-4 text-blue-500 animate-spin shrink-0" />
                  )}

                  <Badge variant="outline" className="text-xs font-mono shrink-0">
                    {sync.jobType}
                  </Badge>

                  <span className="text-muted-foreground">
                    {new Date(sync.startedAt).toLocaleString('pt-BR')}
                  </span>

                  <span className="ml-auto flex items-center gap-2 text-xs text-muted-foreground">
                    {sync.cardsCreated > 0 && (
                      <span className="text-green-600">+{sync.cardsCreated}</span>
                    )}
                    {sync.cardsUpdated > 0 && (
                      <span className="text-blue-600">~{sync.cardsUpdated}</span>
                    )}
                    {sync.errors > 0 && (
                      <span className="text-destructive">{sync.errors} erros</span>
                    )}
                    {sync.cardsSkipped > 0 && (
                      <span>{sync.cardsSkipped} skip</span>
                    )}
                  </span>

                  {sync.errorDetails && (
                    <span className="text-xs text-destructive truncate max-w-[200px]" title={sync.errorDetails}>
                      {sync.errorDetails.substring(0, 80)}...
                    </span>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
